import { useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import { useCallback, useMemo } from "react";
import usePromise from "react-promise-suspense";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { HomepageAiEngineEvents } from "~/components/tracking/google-analytics/types";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";
import { accountAtom } from "~/lib/account/atoms";
import { fetchAiEngineData } from "~/lib/ai-engine/apis";
import { AiEngineDataType } from "~/lib/ai-engine/enums";
import type {
  AiRecommendedForYouResponse,
  AiYouMayHaveMissedResponse,
} from "~/lib/ai-engine/types";

import type { Props } from ".";

export type AIEngineResponse = {
  aiEngineRecommendId: string;
  entityIds: string[];
};

export const useFetchAiArticles = (
  targetVariant: Props["variant"],
  excludeEntityIds: string[],
): AIEngineResponse => {
  const { user } = useAtomValue(accountAtom);

  const result = usePromise(
    async (variant: Props["variant"], excludeIds?: string[]) =>
      fetchAiEngineData<typeof variant>({
        excludeIds,
        type: variant,
        userUuid: user?.uuid,
      }),
    [targetVariant, excludeEntityIds],
  );

  const entityIds = useMemo(() => {
    if (!result?.data) return [];

    switch (targetVariant) {
      case AiEngineDataType.HomeRecommendedForYou: {
        const { results } = result.data as AiRecommendedForYouResponse;
        return [...results].sort((a, b) => a.rank - b.rank).flatMap(data => data.id);
      }
      case AiEngineDataType.HomeYouMayHaveMissed: {
        const { results } = result.data as AiYouMayHaveMissedResponse;
        return [...results].flatMap(data => data.id);
      }
    }
  }, [targetVariant, result?.data]);

  return {
    aiEngineRecommendId: result.data.rec_id,
    entityIds,
  };
};

export const useTracking = (parameters: {
  aiEngineRecommendId: string;
  displayEntityIds: string[];
  variant: Props["variant"];
}) => {
  const { aiEngineRecommendId, displayEntityIds, variant } = parameters;
  const concatenatedEntityIds = displayEntityIds.join(",");

  const widgetName =
    variant === AiEngineDataType.HomeRecommendedForYou
      ? "recommended_for_you"
      : "you_may_have_missed";

  const impressionEvent = useCallback<() => HomepageAiEngineEvents>(
    () => ({
      action: `Impression/${widgetName}` as const,
      category: "Homepage Depth",
    }),
    [widgetName],
  );
  const ga4ImpressionEvent = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: concatenatedEntityIds,
        page_type: "homepage",
        recommendation_id: aiEngineRecommendId,
        widget_name: widgetName,
      },
      subcategory: "widget",
    }),
    [aiEngineRecommendId, concatenatedEntityIds, widgetName],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection<
    HomepageAiEngineEvents,
    RecirculationWidgetImpressionEvent
  >({
    ga4TrackingHandler: sendGA4Tracking,
    gaTrackingHandler: sendGATracking,
    getEvent: impressionEvent,
    getGa4Event: ga4ImpressionEvent,
    options: { isSendGA4Tracking: true, isSendGATracking: true, shouldSendOnce: true },
  });

  const trackClick = useCallback(
    (entityId: string, destinationUrl: string) => {
      sendGATracking({
        action: `Click/${widgetName}` as const,
        category: "Homepage Depth",
        label: entityId,
      });
      sendGA4Tracking({
        action: "click",
        category: "recirculation",
        customized_parameters: {
          article_id: entityId,
          destination_url: destinationUrl,
          list_of_articles: concatenatedEntityIds,
          page_type: "homepage",
          recommendation_id: aiEngineRecommendId,
          widget_name: widgetName,
        },
        subcategory: "widget",
      });
    },
    [aiEngineRecommendId, concatenatedEntityIds, widgetName],
  );

  return {
    captureTrackImpressionEventTargetElement,
    trackClick,
  };
};
